import { useMemo } from 'react'

import {
  matchFilterBetweenDates, matchFilterNumber, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import formatDate from '@functions/formatDate'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ActivityModel, ActivityRequestOptions } from '@models/types'

const watchEntityKeys = ['activities']

export type AgencyActivityFilters = {
  created_date_end?: string,
  created_date_start?: string,
  campaign_id?: number,
  agency_id?: number,
  limit?: number,
}

type UseActivitiesOptions = {
  filters?: AgencyActivityFilters,
  performHttpRequests?: boolean,
  requestOptions?: ActivityRequestOptions,
}

function useAgencyActivities(options: UseActivitiesOptions = {}) {
  const { filters = {}, requestOptions } = options
  const {
    created_date_end: filterCreatedDateEnd,
    created_date_start: filterCreatedDateStart,
    campaign_id: filteredCampaignId,
    agency_id: filteredAgencyId,
    limit: filterLimit,
  } = filters

  const {
    updatedEntities: { activities: activitiesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { activities } = useSelector(reduxState => reduxState.entities)
  const { loadedForKeys, loading: reduxLoading } = useSelector(reduxState => reduxState.activities)

  // Filter redux entities with provided filters
  const filteredActivities = useMemo(() => {
    const filtered = Object.values(activities).filter((activity: ActivityModel) => {
      const {
        // @ts-expect-error TS2345
        dates, recipient_id, recipient_type, recipient: { agency_id } = {},
      } = activity

      const matchCampaignId = recipient_type === 'Campaign'
        ? matchFilterNumber(recipient_id!, filteredCampaignId) : true

      const matchAgencyId = agency_id! ? matchFilterNumber(agency_id!, filteredAgencyId) : true
      const createdAt = formatDate(dates?.created?.date_time, 'dd/MM/yyyy HH:mm')

      const matchCreatedAt = matchFilterBetweenDates(createdAt, filterCreatedDateStart, filterCreatedDateEnd)

      return matchCreatedAt && matchCampaignId && matchAgencyId
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [activitiesUpdatedAt, JSON.stringify(filters)])

  const filteredActivitiesCount = filteredActivities.length
  const hasFilteredActivities = !!filteredActivitiesCount

  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredActivitiesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Generate unique key for this specific query
  const queryKey = JSON.stringify({ ...filtersWithOffset, limit })
  const isAlreadyLoaded = loadedForKeys.includes(queryKey)

  // Initial load with useReduxAction
  useReduxAction(
    'activities',
    'loadAgenciesActivities',
    {
      ...requestOptions,
      ...filtersWithOffset,
      entityKey: queryKey,
      limit,
    },
    [queryKey, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(actionRequestOptions),
      shouldPerformFn: () => performHttpRequests && !isAlreadyLoaded,
      skipLoadedForKeysCheck: true,
    },
  )

  const loading = (reduxLoading && !isAlreadyLoaded)

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredActivities,
    filteredActivitiesCount,
    hasFilteredActivities,
    loading,
  }
}

export type UseAgencyActivitiesPayload = ReturnType<typeof useAgencyActivities>

export default useAgencyActivities
